/**
 * The enterprise project coordinator, the platform's half (mju-ai-workbench
 * docs/plans/2026-09-project-model-end-to-end.md, R4 part 2; JP's decision 3: for a platform
 * project the coordinator lives in AIOE, for a project in the workbench's Personal space in the
 * workbench). AIOE keeps the
 * queue of record and places a team's members on compute; the workbench places, spawns and
 * reports.
 *
 *   GET    /projects/v1/:id/queue                      { seq, ready: [...] }: the tasks ready to be worked, across the project's boards
 *   POST   /projects/v1/:id/queue/claim                { rootId, board, taskId, deviceId, sessionId, ifVersion } → the task; 409 { current }
 *   DELETE /projects/v1/:id/queue/claim/:taskId?rootId=&board=   release it (a session that stopped)
 *   POST   /projects/v1/:id/place                      { teamId, members, rootId, deviceId? } → { placements, refused }
 *   PUT    /projects/v1/:id/settings                   owners: { allowPersonalTeams }
 *   POST   /projects/v1/:id/dispatch                   { rootId, board, taskId, teamId?, deviceId? } → 202 { dispatchId, deviceId, device, status: "sent", task, placement? }
 *   POST   /projects/v1/:id/dispatch/:dispatchId/ack   the device: { ok, sessionId?, message? }
 *
 * Dispatch from the platform: AIOE picks the device (the one named; else the one holding the
 * claim; else the team's lead placed from the team's pools; else the caller's own online
 * desktop), claims the task for it with sessionId "dispatch:<dispatchId>", and tells it with the
 * Control API call `tasks.dispatch` as the person who dispatched. The device re-keys the claim to
 * its real session (a claim from the same device is the same holder) and acks. A device that
 * cannot take it answers an error, and the claim is let go.
 *
 * A claim lives in the task's `metadata.claimedBy { deviceId, sessionId, principal, at }`, so the
 * board says who has it and a stale claim is released like any other change. Words for people:
 * queue, ready, claimed, released, placed.
 *
 * Claims are leases (AIOE 0.33.0; the workbench's Team Work scenarios audit, Appendix F, and its D244). Only
 * optional fields are added, so no schema version moves; a claim without them reads as epoch 0 with a lease of
 * `at` + 600 s.
 *
 *   metadata.claimedBy gains { epoch, renewedAt, leaseUntil, deviceName }
 *     epoch       the fencing token for the task: +1 on every claim, take-over and dispatch, never back
 *                 (the counter is kept in `metadata.claimEpoch`, and why the last claim ended in
 *                 `metadata.claimEnded`; the platform keeps both, a patch cannot change them)
 *     leaseUntil  renewedAt + CLAIM_LEASE_SECONDS (an unacknowledged dispatch: at + DISPATCH_LEASE_SECONDS)
 *     deviceName  the machine's name; no surface shows a device id
 *   A claim is STALE once leaseUntil has passed. It LAPSES a further `claimLapseMinutes` later (project
 *   setting, default 60, owners set 15 to 1440, 0 = never): the scheduled job `queue.lapse` (every minute)
 *   releases it, puts an in-progress task back to todo, audits `queue.lapse` and files a for-you record to the
 *   old holder. An unacknowledged dispatch lapses at its own lease end (300 s): nobody ever started it.
 *
 *   POST   /projects/v1/:id/queue/leases/renew          { leases: [{ rootId, board, taskId, epoch, sessionId? }] } (at most 200)
 *                                                       → { leaseSeconds, renewed: [...], lost: [{ ..., reason, by?, byDeviceName?, at }] }
 *   DELETE /projects/v1/:id/queue/claim/:taskId?rootId=&board=&epoch=
 *                                                       a device names its epoch (else 409 not_holder); a person needs none,
 *                                                       and a person releasing another machine's claim is a take-over
 *   POST   /projects/v1/:id/queue/claim/:taskId/takeover?rootId=&board=
 *                                                       { ifEpoch, reason?, requeue? = true, deviceId?, sessionId?, force? }
 *                                                       → { task, claimedBy | null, previous }
 *
 * Who takes over (409/403/404 answers carry `{ error, message }`, in words): the claim's own person from any
 * device, console or phone; the task's assignee when a person; an owner. An agent acting for the operations
 * manager (`x-aioe-actor`) only once the claim is stale; before that only an owner or the claim's own person,
 * with `force`. A fresh lease is refused `not_stale` unless `force` from an owner or the claim's own person.
 * Refusals: 409 not_stale, epoch_conflict, not_holder, done; 403 forbidden; 404 not_found.
 *
 * Fencing: an agent's board PATCH (`x-aioe-actor`) to status, closure, outcome, workBranch, startCommit or
 * worktreePath on a claimed task carries `x-aioe-claim-epoch` (CLAIM_EPOCH_HEADER, board.ts) matching the
 * claim, else 409 not_holder; checked before the field-wise merge, never merged. Comments, a person's own
 * writes and writes to an unclaimed task are not fenced. A matching fenced write renews the lease.
 *
 * One project, many repositories, part 2 (AIOE 0.34.0; the workbench's Team Work scenarios audit, Appendix G, and its
 * D245). Only optional fields and new routes are added, so no schema version moves.
 *
 *   Waiting across repositories. A repository task's `metadata.waitsOnParts` is `[{ ref, rootId, board, uid, name? }]`
 *   (`partWaitSchema`); each is resolved by uid across the project's boards. A to-do task with an open wait (the part is
 *   not done) is listed under the queue's `waiting` with `waitsOn: QueueWaitsOn[]` and `heldFor` (the sentence), never
 *   under `ready`; claim and dispatch answer 409 `waiting_on_parts` with the same sentence ("T-7 waits for t2 “Add the
 *   endpoint” in api, which is in progress"). A dependency id the task's board does not have is no longer counted as met:
 *   it is listed under `missingDependencies` and waited on (the task is under `waiting`).
 *
 *   The plan travels. A part made for a plan with parts elsewhere carries `metadata.plan` (`taskPlanSchema`:
 *   `{ rootId, planId, ref }`). Claim and dispatch answer 409 `plan_not_approved` ("PLAN-3 in web waits for approval")
 *   while that plan (record `plan:<rootId>`) is a draft, and 409 `plan_abandoned` once it is abandoned. Abandoning a plan
 *   (console, phone or workbench) cancels every task carrying that `metadata.plan` in any repository (status blocked, closure
 *   cancelled), recomputes the roll-ups and audits `plan.abandon` with `{ cancelled: [{ rootId, board, taskId }] }`.
 *
 *   The placed task's identity and branch (AIOE 0.36.0, additive; workbench D250). The request may carry `metadata.uid`
 *   (the uid the placed task carries; kept and answered as `metadata: { uid }`, while `partId` stays the placement's own
 *   id), `branch` (the plan's branch name, for the task's `metadata.branch`) and a `title` on each `waitsOnParts[]` entry
 *   (also spelt `waitsOn[]`; `name` and `title` stand for each other), so a held line reads `waits for t2 “Parse PDF
 *   totals”`. The dispatch to the machine carries all three (`metadata`, `branch`, `waitsOnParts` with `name` and `title`).
 *
 *   Placement is root-aware. `/place` and `/dispatch` consider only devices whose latest registration reports the root
 *   with a running host (`workspaceHostDown` false). A named device without it answers 409 `root_not_held` ("Anna-laptop
 *   does not have api"); when no device online has it, 409 `root_not_on_device` with `{ holders: PlaceHolder[] }` ("Nobody
 *   online has lib: Anna-laptop has it (last seen 14:02)"). A refusing ack still releases the dispatch's claim.
 *
 *   Placing a part on another machine. `POST /projects/v1/:id/parts` (contributors and above; `partRequestSchema`) sends a
 *   `tasks.dispatch` of kind `part.place` (`PART_PLACE_KIND`) to an online device holding the root; the answer is 202
 *   `PartPlaceAnswer` (`state` placed, or waiting_for_device when nobody holding it is online: the part is kept at the home
 *   and placed on the holder's next registration or heartbeat). `GET /projects/v1/:id/parts[?projectTask=<uid>]` lists the
 *   parts placed from here (`PartRecord`), so the project task says "waiting for a machine with api". Audited `part.place`.
 *
 * A person's claim (AIOE 0.35.0; the workbench's D247, B-10). Only optional fields and a new error code; no schema version moves.
 *
 *   A workbench claims a task its person starts (a move to In Progress on the shared board) with the claim's `sessionId`
 *   `person:<personId>` (`personSessionId`; the id is the signed-in person's, 403 `person_mismatch` otherwise) and the person's
 *   principal. The platform writes `metadata.claimedBy.personName` (their name). It is a lease like any other: renewed, released
 *   with its epoch, taken over, lapsed, fenced. The queue lists such a task under `waiting` (never `ready`, never `claimed`),
 *   with `forPerson`, `heldFor` ("Anna is working on T-5") and `claimedBy`, so a console or a phone can release it.
 *   Any other session's claim or dispatch of it, a take-over that sends it to a device (not the person's own) and an owner's
 *   dispatch are refused 409 `persons_task` with `{ message: "T-5 is Anna's, and a person's task is not handed to an agent
 *   without their say.", person: { id, name }, claimedBy }`. The person releases it from any of their devices (a device
 *   needs no epoch match to the holding device for a person's own claim), the console and the phone (DELETE with no epoch,
 *   or take-over). Audited `queue.claim` with `kind: "person"`.
 */
import { z } from "zod";
import { teamPlacementSchema } from "./projects.js";
const keySchema = z.string().min(1).max(200).regex(/^[A-Za-z0-9_.:-]+$/, "letters, digits and _ . : -");
/** What a task asks of the compute that runs it (`task.metadata.requires`). */
export const taskRequiresSchema = z
    .object({
    runtimes: z.array(z.string().min(1).max(100)).max(16).optional(),
    models: z.array(z.string().min(1).max(200)).max(16).optional(),
    tags: z.array(z.string().min(1).max(64)).max(16).optional(),
})
    .strict();
export const claimTaskSchema = z.object({
    rootId: keySchema,
    board: keySchema,
    taskId: z.string().min(1).max(100),
    deviceId: z.string().min(1).max(100),
    sessionId: z.string().min(1).max(200),
    ifVersion: z.number().int().nonnegative(),
});
/** The session prefix of a person's claim (AIOE 0.35.0, D247, B-10). */
export const PERSON_SESSION_PREFIX = "person:";
/** The claim's session for a person. */
export const personSessionId = (personId) => `${PERSON_SESSION_PREFIX}${personId}`.slice(0, 200);
/** Whether a claim is a person's (their session), not an agent's. */
export const isPersonClaim = (claim) => typeof claim?.sessionId === "string" && claim.sessionId.startsWith(PERSON_SESSION_PREFIX);
/** The person a person's claim is for (the id after the prefix). */
export const personOfClaim = (claim) => claim.sessionId.slice(PERSON_SESSION_PREFIX.length);
/** The error code a claim or dispatch of a person's task is refused with (409). */
export const PERSONS_TASK_ERROR = "persons_task";
/** "T-5 is Anna's, and a person's task is not handed to an agent without their say." */
export const personTaskWords = (taskId, name) => `${taskId} is ${name}'s, and a person's task is not handed to an agent without their say.`;
/** A claim's lease, in seconds, from each renewal. */
export const CLAIM_LEASE_SECONDS = 600;
/** An unacknowledged dispatch holds its claim this long (a refusing ack releases it at once). */
export const DISPATCH_LEASE_SECONDS = 300;
/** The project setting `claimLapseMinutes`: how long past its lease a claim waits before the platform releases it. */
export const CLAIM_LAPSE_DEFAULT_MINUTES = 60;
export const CLAIM_LAPSE_MIN_MINUTES = 15;
export const CLAIM_LAPSE_MAX_MINUTES = 1440;
/** The most leases one renewal names. */
export const LEASE_RENEW_MAX = 200;
/** Why a lease is gone: another machine took it, it was released, it lapsed, the machine was revoked, the task is done, there is no such task, or its repository was removed from the project (`root_removed`, AIOE 0.34.0). */
export const LEASE_LOST_REASONS = ["taken_over", "released", "lapsed", "revoked", "done", "not_found", "root_removed"];
export const leaseRefSchema = z.object({
    rootId: keySchema,
    board: keySchema,
    taskId: z.string().min(1).max(100),
    epoch: z.number().int().nonnegative(),
    sessionId: z.string().min(1).max(200).optional(),
});
/** `POST /projects/v1/:id/queue/leases/renew`. */
export const leaseRenewSchema = z.object({ leases: z.array(leaseRefSchema).min(1).max(LEASE_RENEW_MAX) });
/** `POST /projects/v1/:id/queue/claim/:taskId/takeover?rootId=&board=`. */
export const takeoverRequestSchema = z.object({
    ifEpoch: z.number().int().nonnegative(),
    reason: z.string().max(500).optional(),
    /** Put an in-progress task back to todo (the default), or, false, leave its status as it is. Ignored with `deviceId`. */
    requeue: z.boolean().default(true),
    /** Send it to this device (the caller's own, or one that reaches the project): the claim moves to it at epoch + 1. */
    deviceId: z.string().min(1).max(100).optional(),
    sessionId: z.string().min(1).max(200).optional(),
    /** Take it over although the lease is fresh: an owner, or the claim's own person. */
    force: z.boolean().default(false),
});
export const TAKEOVER_VIAS = ["console", "phone", "workbench", "node"];
/** The header a client names its surface in (AIOE addition): "phone" from the phone; absent means the console for a person, the workbench or node for a device. */
export const CLAIM_VIA_HEADER = "x-aioe-via";
/** The notification action a stale-claim record offers: take the task over (params { rootId, board, taskId, epoch }). */
export const QUEUE_TAKEOVER_ACTION_RPC = "aioe.queue.takeover";
/** …and the one that leaves it for now; it only closes the record. */
export const QUEUE_WAIT_ACTION_RPC = "aioe.queue.wait";
export const QUEUE_STALE_DEDUPE_PREFIX = "claim-stale:";
/** A member as the workbench asks to place it: its name and where it may run. */
export const placeMemberSchema = z.object({
    name: z.string().min(1).max(100),
    runsOn: teamPlacementSchema.extend({ node: z.string().min(1).max(100).optional() }).optional(),
});
export const placeRequestSchema = z.object({
    teamId: z.string().min(1).max(100),
    members: z.array(placeMemberSchema).min(1).max(50),
    rootId: keySchema,
    /** The device asking, for a sign-in that has none (the console). */
    deviceId: z.string().min(1).max(100).optional(),
});
/** The Control API method a dispatched task arrives by: `tasks.dispatch { projectId, rootId, board, taskId, teamId?, dispatchId, principal, task, claim }`. */
export const DISPATCH_RPC = "tasks.dispatch";
/** `POST /projects/v1/:id/dispatch/:dispatchId/ack`: the device says whether it started the task. */
export const dispatchAckSchema = z.object({
    ok: z.boolean(),
    sessionId: z.string().min(1).max(200).optional(),
    message: z.string().max(2000).optional(),
});
/** How long a dispatch waits for its ack before the console says "not answered". */
export const DISPATCH_ACK_SECONDS = 60;
/**
 * The fields the platform reads are checked; anything else (a `kind` such as "team.startMember" with its
 * `memberId`, `startedBy`, …) travels to the device's `tasks.dispatch` params unchanged.
 */
export const dispatchRequestSchema = z.looseObject({
    rootId: keySchema,
    board: keySchema,
    taskId: z.string().min(1).max(100),
    /** The team to run it, when the platform should choose where; the board's team when left out. */
    teamId: z.string().min(1).max(100).optional(),
    /** The device to send it to, when the caller chooses. */
    deviceId: z.string().min(1).max(100).optional(),
    ifVersion: z.number().int().nonnegative().optional(),
});
/**
 * The task-less form: a person presses Start on a team whose member runs on another workbench.
 * `POST /projects/v1/:id/dispatch { kind: "team.startMember", rootId, teamId, memberId, startedBy?, deviceId? }`:
 * no task is looked up and no claim taken; the platform forwards it to the device as the same
 * `tasks.dispatch` call with these params and a `dispatchId`, answers 202 `{ dispatchId }`, and the
 * device acks at `POST …/dispatch/:dispatchId/ack` as for a task. Audited `member.dispatch` and
 * `member.dispatch.ack`.
 */
export const MEMBER_START_KIND = "team.startMember";
export const memberStartSchema = z.looseObject({
    kind: z.literal(MEMBER_START_KIND),
    rootId: keySchema,
    teamId: z.string().min(1).max(100),
    memberId: z.string().min(1).max(200),
    /** The person who pressed Start; the caller when left out. */
    startedBy: z.string().min(1).max(200).optional(),
    /** The device to send it to; else the member's placement from the team's pools, else the caller's own desktop. */
    deviceId: z.string().min(1).max(100).optional(),
});
/** The workbench's names for the same things (its shared/schemas/coordinator.ts is kept identical). */
export const TEAM_START_MEMBER_KIND = MEMBER_START_KIND;
export const memberStartRequestSchema = memberStartSchema;
/** What the device receives in `tasks.dispatch` params for a member start. */
export const memberStartParamsSchema = z.looseObject({
    kind: z.literal(MEMBER_START_KIND),
    projectId: z.string().min(1),
    rootId: keySchema,
    deviceId: z.string().min(1).max(100),
    teamId: z.string().min(1).max(100),
    memberId: z.string().min(1).max(200),
    memberName: z.string().max(200).optional(),
    startedBy: z.string().min(1).max(200).optional(),
    dispatchId: z.string().min(1),
    principal: z.string().min(1),
});
/** Priority order for the queue: what should be worked first. */
export const PRIORITY_RANK = { critical: 0, high: 1, medium: 2, low: 3 };
// ── One project, many repositories, part 2 (AIOE 0.34.0) ─────────────────
/** `metadata.waitsOnParts[]`: a part in another repository (or board) of the project that this task waits on. */
export const partWaitSchema = z.object({
    ref: z.string().max(200),
    rootId: keySchema,
    board: keySchema,
    uid: z.string().min(1).max(200),
    name: z.string().max(200).optional(),
    /** AIOE 0.36.0: the part's title, so the held line reads `waits for t2 “Parse PDF totals”`; `name` and `title` stand for each other when only one is sent. */
    title: z.string().max(200).optional(),
});
/** `metadata.plan` on a part: the plan, in the repository that made it, that the part belongs to. */
export const taskPlanSchema = z.object({ rootId: z.string().max(200), planId: z.string().min(1).max(200), ref: z.string().max(100) });
/** The refusals this round adds (409 `{ error, message }`). */
export const PART_REFUSALS = ["waiting_on_parts", "plan_not_approved", "plan_abandoned", "root_not_held", "root_not_on_device"];
const WAIT_STATUS_WORDS = { backlog: "in the backlog", todo: "to do", "in-progress": "in progress", review: "in review", blocked: "blocked", done: "done", cancelled: "cancelled", missing: "not on its board yet", "handed-on": "handed on" };
/** "t2 “Add the endpoint” in api, which is in progress". */
export function waitsOnWords(w) {
    const state = w.closure?.kind === "cancelled" ? "cancelled" : w.closure?.kind === "handed-on" ? "handed-on" : w.status;
    return `${w.id} “${w.title}” in ${w.rootName}, which is ${WAIT_STATUS_WORDS[state] ?? state.replace(/[-_]/g, " ")}`;
}
/** "T-7 waits for t2 “Add the endpoint” in api, which is in progress" (more than one: each, joined with "; "). */
export function waitingOnPartsSentence(taskId, waits) {
    return `${taskId} waits for ${waits.map(waitsOnWords).join("; ")}`;
}
/** "PLAN-3 in web waits for approval" / "PLAN-3 in web was abandoned". */
export function planNotLiveSentence(state, ref, rootName) {
    return state === "draft" ? `${ref} in ${rootName} waits for approval` : `${ref} in ${rootName} was abandoned`;
}
/** "Nobody online has lib: Anna-laptop has it (last seen 14:02)". Times are UTC, as the platform keeps them. */
export function rootNotOnDeviceSentence(rootName, holders) {
    if (!holders.length)
        return `Nobody online has ${rootName}, and no machine has reported it yet`;
    const who = holders.map((h) => `${h.deviceName}${h.lastSeenAt ? ` (last seen ${h.lastSeenAt.slice(11, 16)})` : ""}`);
    return `Nobody online has ${rootName}: ${who.join(", ")} ${holders.length === 1 ? "has it" : "have it"}`;
}
/** The Control API `tasks.dispatch` params `kind` for placing a part on a machine that holds the repository. */
export const PART_PLACE_KIND = "part.place";
/** `POST /projects/v1/:id/parts`. */
export const partRequestSchema = z.object({
    /** The project task (its uid, on the project board) the part belongs to. */
    projectTask: z.string().min(1).max(200),
    rootId: keySchema,
    title: z.string().min(1).max(1000),
    description: z.string().max(200_000).default(""),
    ref: z.string().max(200).optional(),
    team: z.string().max(200).optional(),
    specialist: z.string().max(200).optional(),
    role: z.string().max(200).optional(),
    originTeam: z.string().max(200).optional(),
    assignee: z.string().max(200).optional(),
    plan: taskPlanSchema.optional(),
    waitsOnParts: z.array(partWaitSchema).max(50).optional(),
    /** AIOE 0.36.0: a spelling of `waitsOnParts` (the workbench's `waitsOn[]`); merged into it, entries with `title`. */
    waitsOn: z.array(partWaitSchema).max(50).optional(),
    /** AIOE 0.36.0: the placed task's own uid, kept as sent and answered back; the platform's `partId` stays the placement's id. */
    metadata: z.object({ uid: z.string().min(1).max(200) }).passthrough().optional(),
    /** AIOE 0.36.0: the plan's branch name, carried to the placed task's `metadata.branch`. */
    branch: z.string().max(250).optional(),
});
//# sourceMappingURL=coordinator.js.map